/**
 * What a state is called out loud (INCO-414).
 *
 * Mirrors `app/models/state_label.rb`, which is the source of truth because
 * the states are Ruby's. `spec/models/state_label_spec.rb` parses this file and
 * fails if the two disagree — a mirror nothing compares is a second copy
 * waiting to drift.
 *
 * Grouped, because the same word means different things in two places:
 * `failed` on an extraction is "Couldn't read", and `failed` on a dissemination
 * is "Not delivered".
 */
export const STATE_LABELS: Record<string, Record<string, string>> = {
  offer: {
    received: "Queued",
    transcribing: "Transcribing",
    extracting: "Extracting",
    extracted: "Read",
    needs_review: "Needs review",
    failed: "Couldn't read",
  },
  deal: {
    lead: "Lead",
    qualified: "Qualified",
    in_progress: "In progress",
    won: "Won",
    lost: "Lost",
  },
  dissemination: {
    pending: "Queued",
    sent: "Sent",
    viewed: "Opened",
    inquired: "Replied",
    failed: "Not delivered",
  },
  opportunity: {
    active: "Active",
    stale: "Gone quiet",
    closed: "Closed",
  },
  mandate: {
    requested: "Pending",
    confirmed: "Confirmed",
    declined: "Rejected",
    expired: "Expired",
    revoked: "Withdrawn",
  },
  attestation: {
    pending: "Waiting on them",
    confirmed: "Agreed",
    disputed: "Disputed",
    expired: "Run out",
  },
  network_dissemination: {
    sent: "Sent",
    opened: "Opened",
    claimed: "Claimed",
    declined: "Declined",
    withdrawn: "Withdrawn",
  },
  connection: {
    pending: "Waiting",
    accepted: "Connected",
    declined: "Declined",
    blocked: "Blocked",
  },
  brief: {
    draft: "Draft",
    live: "Live",
    unpublished: "Unpublished",
  },
  held_brief: {
    live: "Live",
    unpublished: "Unpublished",
    withdrawn: "Withdrawn",
    requested: "Awaiting approval",
  },
  marketplace_listing: {
    draft: "Draft",
    live: "Live",
    paused: "Paused",
    closed: "Closed",
  },
  marketplace_access_request: {
    pending: "Waiting on them",
    accepted: "Accepted",
    declined: "Declined",
    withdrawn: "Withdrawn",
  },
  marketplace_fee: {
    invoiced: "Invoiced",
    paid: "Paid",
    waived: "Waived",
    disputed: "Disputed",
  },
  escrow: {
    open: "Waiting for deposits",
    depositing: "Depositing",
    complete: "Complete",
    released: "Released",
    withdrawn: "Withdrawn",
  },
}

export type StateGroup = keyof typeof STATE_LABELS

/**
 * Sentence-cases anything unnamed rather than throwing. A state nobody has
 * named yet should read as badly as it deserves and no worse — a card is not
 * the place to discover a missing entry.
 */
export function stateLabel(group: string, value?: string | null): string {
  const key = String(value ?? "")
  if (!key) return ""

  const named = STATE_LABELS[group]?.[key]
  if (named) return named

  const spaced = key.replace(/_/g, " ")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
